import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.odt,.rtf,image/*,application/pdf";
const ALLOWED = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "doc", "docx", "xls", "xlsx", "csv", "txt", "ppt", "pptx", "odt", "rtf"];
const MAX = 10 * 1024 * 1024;

export function validateFile(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!ALLOWED.includes(ext)) return "unsupported type — use PDF, image, Word, Excel, CSV or text";
  if (file.size > MAX) return "too large (max 10MB)";
  return null;
}

// Upload already-selected File objects to {basePath}/attachments. Resilient: reports per-file errors.
export async function uploadFiles(basePath, files) {
  for (const file of files) {
    const err = validateFile(file);
    if (err) { toast.error(`${file.name}: ${err}`); continue; }
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`${basePath}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || "upload failed"}`);
    }
  }
}

// Stages File objects locally (used inside Add dialogs before the record exists).
export function FileStager({ files, setFiles }) {
  const ref = useRef();
  const onPick = (picked) => {
    const ok = [];
    for (const f of picked) {
      const e = validateFile(f);
      if (e) toast.error(`${f.name}: ${e}`); else ok.push(f);
    }
    setFiles([...(files || []), ...ok]);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div>
      <input ref={ref} type="file" multiple accept={ACCEPT} hidden onChange={(e) => onPick(Array.from(e.target.files))} data-testid="stager-input" />
      <button type="button" onClick={() => ref.current?.click()}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#2EA3FD] border border-dashed border-slate-300 rounded-md px-3 py-1.5" data-testid="stager-add-btn">
        <Paperclip size={12} />Add document(s)
      </button>
      <div className="mt-1.5 space-y-1">
        {(files || []).map((f, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs" data-testid={`staged-file-${i}`}>
            <FileText size={12} className="text-slate-400 shrink-0" />
            <span className="truncate max-w-[220px]">{f.name}</span>
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-300 hover:text-rose-500 shrink-0" aria-label="Remove"><X size={12} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Lists attachments on an existing record + lets you upload/remove. `showList=false` shows only the upload button.
export function Attachments({ basePath, items = [], canManage = true, label = "Attach document", showList = true }) {
  const [list, setList] = useState(items);
  const [busy, setBusy] = useState(false);
  const ref = useRef();

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const err = validateFile(file);
        if (err) { toast.error(`${file.name}: ${err}`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`${basePath}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setList((l) => [...l, data]);
      }
      toast.success("Document uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = async (aid) => {
    try {
      await api.delete(`${basePath}/attachments/${aid}`);
      setList((l) => l.filter((a) => a.id !== aid));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-1.5" data-testid="attachments">
      {showList && list.map((a) => (
        <div key={a.id} className="flex items-center gap-1.5 text-xs" data-testid={`attachment-${a.id}`}>
          <FileText size={13} className="text-slate-400 shrink-0" />
          <a href={`${BACKEND}/api${basePath}/attachments/${a.id}/file`} target="_blank" rel="noreferrer"
             className="text-[#2EA3FD] hover:underline truncate max-w-[180px]" title={a.filename}>{a.filename}</a>
          {canManage && (
            <button onClick={() => remove(a.id)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`attachment-del-${a.id}`} aria-label="Remove attachment"><X size={12} /></button>
          )}
        </div>
      ))}
      {canManage && (
        <>
          <input ref={ref} type="file" multiple accept={ACCEPT} hidden onChange={(e) => upload(Array.from(e.target.files))} data-testid="attachment-input" />
          <button onClick={() => ref.current?.click()} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#2EA3FD] disabled:opacity-50" data-testid="attachment-upload-btn">
            <Paperclip size={12} />{busy ? "Uploading…" : label}
          </button>
        </>
      )}
    </div>
  );
}
